import { Carousel } from "hec/components/ui/carousel";
import type * as React from "react";

import type { RegisteredComponent } from "@builder.io/sdk-react";
import { Blocks } from "@builder.io/sdk-react";

import { cn } from "~/lib/utils";

import { CarouselContentDefinition } from "./carouselContent";

/**
 * SFCC-11736 — deprecated bare-named `Carousel` / `CarouselContent` (the
 * hec-composable pre-`v1:` shape). Some production Builder entries — e.g. the
 * HSA `/learning-center` landing "Featured Articles" — author the carousel with
 * the BARE names and store the carousel's child blocks under `options.content`
 * (NOT as `children`, which the current `v1: Carousel` reads). Composable
 * registers these deprecated blocks, so the same entry renders there; Workers
 * never ported them, so it rendered blank. Registering them here restores
 * parity off the same Builder content. Additive only: these are new names, the
 * widely-used `v1: Carousel`/`v1: CarouselContent` are untouched, and
 * `hideFromInsertMenu` keeps them out of the editor (author new content with
 * `v1:`). Mirrors `hec-composable/hec/builder/blocks/deprecated/carousel/*`.
 */
interface DeprecatedCarouselProps {
  content?: unknown[];
  loop?: boolean;
  slidesToScroll?: number;
  align?: "start" | "center" | "end";
  builderBlock?: { id: string };
  attributes?: Record<string, unknown> & { className?: string };
}

export const DeprecatedCustomCarousel = (props: DeprecatedCarouselProps) => {
  const { content, loop, slidesToScroll, align, builderBlock, attributes } =
    props;
  const { className, ...restAttributes } = attributes || {};
  return (
    <Carousel
      opts={{ loop, slidesToScroll, align }}
      className={cn("mx-auto w-[80%]", className)}
      {...restAttributes}
      data-testid="carousel-Carousel"
    >
      <Blocks
        path="component.options.content"
        blocks={content as never}
        parent={builderBlock?.id}
        data-testid="carousel-Blocks"
      />
    </Carousel>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const DeprecatedCarouselDefinition: RegisteredComponent = {
  component: DeprecatedCustomCarousel,
  name: "Carousel",
  hideFromInsertMenu: true,
  canHaveChildren: true,
  childRequirements: {
    message:
      "Children of 'Carousel' must be 'CarouselButton', 'CarouselContentNav', or 'CarouselContent'",
    query: {
      "component.name": {
        $in: ["CarouselButton", "CarouselContent", "CarouselContentNav"],
      },
    },
  },
  noWrap: true,
  shouldReceiveBuilderProps: {
    builderBlock: true,
  },
  defaultStyles: {
    marginLeft: "auto",
    marginRight: "auto",
    width: "80%",
  },
  inputs: [
    {
      name: "align",
      type: "string",
      enum: ["start", "center", "end"],
      defaultValue: "start",
      helperText: "Align the slides relative to the carousel viewport.",
    },
    {
      name: "loop",
      type: "boolean",
      defaultValue: true,
      helperText: "Enables infinite looping.",
    },
    {
      name: "slidesToScroll",
      type: "number",
      defaultValue: 1,
      helperText: "Group slides together.",
    },
  ],
};

// Runtime shape is identical to `v1: CarouselContent` (`slides[].content`), so
// reuse that component — only the registered (bare) name differs.
// eslint-disable-next-line react-refresh/only-export-components
export const DeprecatedCarouselContentDefinition: RegisteredComponent = {
  ...CarouselContentDefinition,
  name: "CarouselContent",
  hideFromInsertMenu: true,
};
